// ============================================================
// MANIC MEETER — English content: events, questions, minigames
// ============================================================
// Text only. No logic and no balance — those live in src/events.ts.

import {
  BrainstormingChallenge, BuzzwordBingoChallenge, CodeReviewChallenge,
  EmailSortierenChallenge, KalenderTetrisChallenge, KiHalluzinationChallenge,
  Question, RandomEncounter, SimonSaysChallenge,
} from "../../types";
import type { AddressMethod, AddressPhraseContext, ProcrastinationActivity } from "../contract";

// ---- Procrastinating ----

export const PROCRASTINATION_ACTIVITIES: ProcrastinationActivity[] = [
  {
    action: "You scroll through your phone inconspicuously. Fatigue drops.",
    caughtAction: "You were caught scrolling! Excruciating silence.",
  },
  {
    action: "You doodle small shapes on your notepad. Fatigue drops.",
    caughtAction: "Somebody noticed your doodles! 'Very creative...'",
  },
  {
    action: "You secretly write a shopping list. Fatigue drops.",
    caughtAction: "Somebody reads your shopping list aloud! 'Oat milk, loo roll...'",
  },
  {
    action: "You type quietly on your laptop — it looks like notes, it is Reddit. Fatigue drops.",
    caughtAction: "The team lead walks past behind you and sees your Reddit feed! Caught!",
  },
  {
    action: "You stare out of the window and dream of going home. Fatigue drops.",
    caughtAction: "You are yanked out of your daydream! 'Everything all right there?'",
  },
  {
    action: "You fiddle with your keyring under the table. Fatigue drops.",
    caughtAction: "The jangling was too loud. Everyone is staring at you.",
  },
  {
    action: "You count the ceiling tiles in the conference room. Forty-seven. Fatigue drops.",
    caughtAction: "'Why are you staring at the ceiling?' — Everyone looks up.",
  },
  {
    action: "You text a friend under the table. Fatigue drops.",
    caughtAction: "Your phone vibrates loudly. Everyone knows.",
  },
  {
    action: "You mentally reorder your Spotify playlist. Fatigue drops.",
    caughtAction: "'Are you singing??' — You had been humming without noticing.",
  },
  {
    action: "You calculate how many meetings you still have to survive in this life. Fatigue drops.",
    caughtAction: "Your horrified expression is noticed. 'Everything okay?'",
  },
  {
    action: "You optimise next week's grocery run in your head. Fatigue drops.",
    caughtAction: "'Did you just mutter something about avocados?' — Caught.",
  },
  {
    action: "You pretend to take notes but draw small cats instead. Fatigue drops.",
    caughtAction: "The intern spots your cat drawings. 'Awwww!' — Everyone looks.",
  },
  {
    action: "You have the AI draft your contribution for you. Fatigue drops.",
    caughtAction: "You read the AI's answer aloud. It begins with 'Certainly! Here is a possible contribution:'",
  },
  {
    action: "You generate pictures of your colleagues as medieval knights. Fatigue drops.",
    caughtAction: "Your screen shows the boss as a knight. With six fingers on the lance.",
  },
  {
    action: "You ask the AI how to leave a meeting politely. Fatigue drops.",
    caughtAction: "Somebody reads your prompt over your shoulder: 'how do i get out of here without anyone noticing'",
  },
  {
    action: "You have the meeting summarised live instead of listening. Fatigue drops.",
    caughtAction: "'So what was your last point?' Your summary does not know either.",
  },
  {
    action: "You have the assistant generate excuses for Friday. Fatigue drops.",
    caughtAction: "Your screen reads '10 credible reasons to cancel a meeting'. Number 3 is highlighted.",
  },
  {
    action: "You argue with a chatbot about whether it is conscious. Fatigue drops.",
    caughtAction: "'Who are you typing to so intently?' — Nobody. Technically.",
  },
  {
    action: "You have the AI explain your own ticket back to you. Fatigue drops.",
    caughtAction: "The team lead watches you having your own work explained to you.",
  },
];

// ---- Questions in the board meeting ----

export const BOSS_QUESTIONS: Question[] = [
  {
    prompt: "The boss asks: 'Why are we 15% below plan on revenue?'",
    answers: ["I'd like to propose a task force for that.", "Because the plan was unrealistic.", "I thought that was a stretch goal?"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The Scrum Master asks: 'How many story points does the blue ticket have?'",
    answers: ["Forty-two, as always.", "Story points are only indicative anyway!", "We'll have to discuss that in refinement."],
    correctIndex: 2,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The Product Owner asks: 'When will the feature be finished?'",
    answers: ["When it's finished.", "How are we defining 'finished'?", "Yesterday."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "HR asks: 'How do you find the company culture?'",
    answers: ["What culture?", "I haven't read the culture handbook yet.", "We live those values every day."],
    correctIndex: 2,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Marketing asks: 'How do we increase the conversion rate?'",
    answers: ["Just make the button bigger.", "A/B tests on the landing page.", "More pop-ups!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The team lead asks: 'Who broke the build?'",
    answers: ["Git blame says: you.", "I'll investigate that in the post-mortem.", "It wasn't me, I swear!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The boss asks: 'What is our USP?'",
    answers: ["Our unique tech stack and team spirit.", "That we are somehow still here.", "USB?"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The head of department asks: 'Where do we stand on the quarterly target?'",
    answers: ["What quarterly target?", "We are on track according to current tracking.", "We have quarterly targets?"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The boss asks: 'How can we become more innovative?'",
    answers: ["A structured innovation programme with sprints.", "Fewer meetings, more doing.", "Just try random things."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The client manager asks: 'The client is threatening to terminate. Now what?'",
    answers: ["Threaten to terminate first.", "Find a new client.", "We set up an escalation meeting and deliver an action plan."],
    correctIndex: 2,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The head of department asks: 'Why is the department over budget?'",
    answers: ["We have identified the variance and are correcting for it.", "Because everything got more expensive.", "Budget? We had a budget?"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "HR asks: 'How can we reduce staff turnover?'",
    answers: ["More money. Done.", "Lock the door.", "We analyse the exit interviews systematically."],
    correctIndex: 2,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The boss asks: 'Where do we see ourselves in five years?'",
    answers: ["Market leader in our segment, per the strategy plan.", "In a different meeting, hopefully.", "Still here, probably."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The boss asks: 'How will the competition react?'",
    answers: ["Not at all, they don't know who we are.", "We monitor the market and respond agilely.", "They have the same problems."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Marketing asks: 'Can we build AI in somewhere?'",
    answers: ["Sure, stick a ChatGPT logo on everything.", "AI already makes the coffee.", "We're evaluating use cases with the tech team."],
    correctIndex: 2,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The CTO asks: 'How do we make sure our AI output is correct?'",
    answers: ["A human checks before anything goes out.", "We trust the model.", "We have a second AI check it."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The CFO asks: 'What is the AI costing us per month?'",
    answers: ["I don't know exactly.", "Less than this meeting.", "The breakdown is in the wiki, I'll send it over."],
    correctIndex: 2,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The board asks: 'So is AI going to replace roles here?'",
    answers: ["It shifts tasks, not people.", "Only the intern's.", "The AI decides that."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The board asks: 'How do we measure the return on our AI investment?'",
    answers: ["In hours saved, set against the licence costs.", "It cannot be measured.", "By how the team feels."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The CTO asks: 'What happens if the vendor doubles the price tomorrow?'",
    answers: ["Then we pay.", "We have evaluated an exit option.", "I have not thought about that."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
];

// ---- Questions in normal meetings ----

export const MEETING_QUESTIONS: Question[] = [
  {
    prompt: "Somebody asks: 'Should we discuss this from first principles again?'",
    answers: ["Yes, let's go right back to the start!", "No, we've been through all of it already.", "I'd have another five points..."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The team lead asks: 'Do we need a follow-up meeting for this?'",
    answers: ["Absolutely, ideally with twenty people!", "No, we can settle that on Slack.", "I'd suggest a whole meeting series."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Does anyone have another item for the agenda?'",
    answers: ["Yes, I've got something!", "No, I think we're done.", "I've actually prepared a whole presentation!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The Scrum Master asks: 'Should we revise the definition of done again?'",
    answers: ["Could we take that offline?", "Yes! Let's spend 45 minutes on it now.", "I have a twelve-point list here..."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Shall we go deeper on this topic?'",
    answers: ["Definitely, it needs at least an hour.", "Better not, time is tight.", "I'll just fetch my documents!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "A colleague says: 'I think we're talking past each other. Let's start again.'",
    answers: ["Good idea!", "I think we actually agreed. Shall we move on?", "Yes, and this time with a whiteboard!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Shall I show my slides on this?' asks the marketing guru.",
    answers: ["Yes, all forty slides please!", "No need, the key points are clear.", "Could you print them out as well?"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The Product Owner asks: 'Should we walk through all the user stories again?'",
    answers: ["Every single one, with all acceptance criteria!", "The top three are enough, we'll do the rest async.", "I've got five new stories as well!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Could we quickly invite all the stakeholders?' asks the head of department.",
    answers: ["Better to decide in a small group.", "Yes, and the board while we're at it!", "I'll send an invitation to all fifty."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Somebody says: 'That reminds me of a similar project back in 2019...'",
    answers: ["Oh, do tell!", "Interesting — but let's stay on the current topic.", "Do you have a case study on that?"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Shall we write a pros and cons list on the board?'",
    answers: ["No, let's vote and move on.", "Yes! And then a SWOT analysis!", "I'll get the post-its!"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The HR manager asks: 'Shall I read out the compliance guidelines again?'",
    answers: ["We know them, thank you.", "Yes please, from the beginning!", "Is there an audiobook version?"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Shall we set up a working group for this?'",
    answers: ["Great idea! With a weekly Jour Fixe!", "No, take the decision and implement it.", "Yes, and a steering committee too!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The boss asks: 'Should I explain my vision in detail once more?'",
    answers: ["No thank you, we've understood it.", "Yes, ideally with anecdotes!", "Could we do a separate strategy offsite about it?"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Shall we pick this topic up again in the next retro?'",
    answers: ["No, it's settled.", "Yes, and a preparation meeting beforehand!", "Definitely, with external facilitation!"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "A colleague asks: 'Should I explain the full context?'",
    answers: ["Yes please, right from the beginning of time!", "A short summary is enough.", "Could you write documentation about it? For the next meeting?"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Shall we ask everyone involved individually?' asks the team lead.",
    answers: ["No, we have consensus.", "Yes, everyone will have something to add!", "And then again in breakout groups!"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Shall I rework the data and present it tomorrow?'",
    answers: ["Yes, and with three different chart types please!", "The numbers are clear enough, we can decide.", "Better make a dashboard out of it!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The intern asks: 'Could somebody explain what this is about?'",
    answers: ["Sure! So, it all started back in 2017...", "Briefly: we're deciding X. Details by chat.", "I'll write you a ten-page summary."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Could we go through the last meeting's notes again?'",
    answers: ["Page by page, naturally!", "No, the action items are settled.", "I'll print them and we'll read together!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The Scrum Master asks: 'How do we handle the technical debt?'",
    answers: ["Just ignore it, that's worked so far.", "We reserve 20% of every sprint for tech debt.", "Rewrite everything! From scratch!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Who is taking the client presentation?'",
    answers: ["I'll do it — I've prepared properly.", "The intern, for practice!", "Can we cancel it?"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The boss asks: 'How do we ensure quality?'",
    answers: ["Just test more!", "We don't. YOLO.", "Through automated tests, code reviews and clear standards."],
    correctIndex: 2,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'What do we do if the deploy goes wrong?'",
    answers: ["Panic!", "Activate the rollback plan we defined in advance.", "Deploy again and hope."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The designer asks: 'Why doesn't the product look like the mockup?'",
    answers: ["We're working with the design team on the implementation.", "It just is what it is.", "Mockups are only suggestions."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Should we abort the sprint?'",
    answers: ["Yes, immediately!", "Absolutely not, we're seeing this through!", "Only if the sprint goal is no longer achievable."],
    correctIndex: 2,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Marketing asks: 'How do we communicate the new feature delay?'",
    answers: ["We don't. Just stay quiet.", "Transparently and proactively, with an updated timeline.", "On social media we blame engineering."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'How do we improve our code quality?'",
    answers: ["Introduce pair programming and regular reviews.", "Just write less code.", "More comments! Comments everywhere!"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The team lead asks: 'What was the biggest blocker this week?'",
    answers: ["The meetings.", "Unclear requirements — we need a refinement.", "I had no blockers, everything's great!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Microservices or monolith?'",
    answers: ["Microservices! Always!", "Monolith! Always!", "It depends on our actual requirements and team size."],
    correctIndex: 2,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "HR asks: 'How do we integrate new team members better?'",
    answers: ["Just let them get on with it, learning by doing!", "A structured onboarding programme with a buddy system.", "A LinkedIn profile is induction enough."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'The feature has been overdue for three sprints. Now what?'",
    answers: ["Reduce the scope and ship the core.", "Never mind, it'll be fine.", "Throw more developers at it!"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The Product Owner asks: 'How do we prioritise the next features?'",
    answers: ["Whoever shouts loudest goes first.", "On business value and effort, using an impact matrix.", "Eeny, meeny, miny, moe."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'The client wants the feature by tomorrow. What do we say?'",
    answers: ["Sure, no problem!", "We communicate a realistic timeline with the reasoning.", "Impossible! Tell him to find another supplier!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The backend dev asks: 'Do we really need a database migration?'",
    answers: ["Yes, the schema has to change for the new features.", "Nah, just bolt on some new columns.", "Migration? Just make a new database!"],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'What is the best way to document our API?'",
    answers: ["We don't, the code is self-explanatory.", "An OpenAPI spec with automatic generation.", "In a Word document. Distributed by email."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The head of department asks: 'Why does the team need a new tool?'",
    answers: ["Because everyone else uses it.", "The current tool measurably slows down our workflows.", "Because the old one is ugly."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'What is the best way to deal with legacy code?'",
    answers: ["Delete it all and start again.", "Refactor incrementally with tests as a safety net.", "Don't touch it. Ever. Never."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The boss asks: 'Why do we have so many open bugs?'",
    answers: ["Bugs? Those are features!", "We've introduced bug triage and prioritise by severity.", "QA tests too well."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Should we build AI into our product?'",
    answers: ["Yes! Everywhere! Now! AI makes everything better!", "We evaluate concrete use cases with measurable benefit.", "No, AI is just hype."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The boss asks: 'Couldn't we just have the AI do that?'",
    answers: ["Yes, I'll generate it right now.", "Sure, I'll send it over later.", "I'll build us our own agent for it by tomorrow!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Did anyone proofread this before it went to the client?'",
    answers: ["The AI wrote it, so yes.", "Yes, it's all fine.", "I'll have a second AI check it, to be safe."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The Scrum Master asks: 'Shall we run an AI workshop for everyone?'",
    answers: ["Yes, two full days ideally!", "A short wiki page would do.", "I could run the workshop!"],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'How much time does the AI actually save us?'",
    answers: ["About as much as we spend prompting it.", "At least 40 percent, I read somewhere.", "I'll run a three-month study on it."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "Marketing asks: 'Should we put AI-assisted copy on the website?'",
    answers: ["Only if it's true.", "Yes, everyone does it!", "I'll write a press release about it right away."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Who is actually liable when the AI gets it wrong?'",
    answers: ["The AI.", "Whoever sent it out.", "We'll sort that out when it happens."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "The team lead asks: 'Should we collect our prompts centrally?'",
    answers: ["A shared folder will do.", "We'll need a dedicated tool and a steering group.", "I'll build us a platform for it."],
    correctIndex: 0,
    wrongPenaltyRounds: 1,
  },
  {
    prompt: "'Has anyone compared the AI output against the source?'",
    answers: ["There was no source.", "Yes, it matches.", "I didn't have time for that."],
    correctIndex: 1,
    wrongPenaltyRounds: 1,
  },
];

// ---- Brainstorming: type the word letter by letter ----

export const BRAINSTORMING_WORDS: BrainstormingChallenge[] = [
  { word: "SynergyEffect", timeMs: 10000, context: "Marketing shouts: 'Quick brainstorm! Type the buzzword!'" },
  { word: "DigitalTransformation", timeMs: 12000, context: "The boss wants to see innovation! Type fast!" },
  { word: "CustomerCentricity", timeMs: 11000, context: "'Kundenzentrierung' in English, please!" },
  { word: "Scalability", timeMs: 10000, context: "The architect needs input — type quickly!" },
  { word: "Agility", timeMs: 7000, context: "The Scrum Master wants to hear buzzwords!" },
  { word: "InnovationCulture", timeMs: 11000, context: "HR would like to put that on the careers page!" },
  { word: "ValueChain", timeMs: 12000, context: "The head of department is presenting — help them out!" },
  { word: "Disruption", timeMs: 8000, context: "Somebody has thrown the word into the room!" },
  { word: "ParadigmShift", timeMs: 11000, context: "The boss is philosophising — join in!" },
  { word: "ProcessOptimisation", timeMs: 12000, context: "The lean management team insists on participation!" },
  { word: "Stakeholder", timeMs: 9000, context: "Who are our stakeholders? Type it!" },
  { word: "ResourcePlanning", timeMs: 11000, context: "The project lead wants to update the board!" },
  { word: "Compliance", timeMs: 8000, context: "HR is asking for the magic word!" },
  { word: "Benchmarking", timeMs: 9000, context: "Marketing needs numbers — and buzzwords!" },
  { word: "Sustainability", timeMs: 10000, context: "The ESG report has to be finished — quickly!" },
  { word: "Microservices", timeMs: 9000, context: "The backend dev has an architecture idea!" },
  { word: "Retrospective", timeMs: 10000, context: "What did we learn? Type it!" },
  { word: "Automation", timeMs: 10000, context: "DevOps would like to automate everything!" },
  { word: "Prioritisation", timeMs: 10000, context: "The PO is sorting the backlog — help out!" },
  { word: "CustomerSatisfaction", timeMs: 12000, context: "The support team is asking for the KPI!" },
  { word: "Hallucination", timeMs: 11000, context: "The boss has a new favourite word. Type it!" },
  { word: "ContextWindow", timeMs: 11000, context: "The backend dev explains why the AI forgets half of it!" },
  { word: "LanguageModel", timeMs: 10000, context: "'What's the proper term again?' — Type it before somebody says LLM!" },
  { word: "PromptOptimisation", timeMs: 12000, context: "Round four on the same prompt. Type along!" },
  { word: "DataQuality", timeMs: 11000, context: "The most inconvenient answer to every AI question. Type it!" },
];

// ---- Simon Says ----

export const SIMON_SAYS_CHALLENGES: SimonSaysChallenge[] = [
  { sequence: ["↑", "↓", "←", "→", "↑"], timeMs: 7000, context: "Stand, sit, left, right, stand — team building!" },
  { sequence: ["→", "→", "↓", "↑", "←"], timeMs: 7000, context: "The Scrum Master runs an energiser: 'Everybody join in!'" },
  { sequence: ["↑", "↑", "↓", "↓"], timeMs: 6000, context: "Stand, stand, sit, sit — office fitness!" },
  { sequence: ["←", "→", "←", "→", "↑", "↓"], timeMs: 8000, context: "Left-right yoga with the HR manager!" },
  { sequence: ["↑", "←", "↓", "→"], timeMs: 6000, context: "Turn clockwise — the boss demonstrates!" },
  { sequence: ["↓", "↓", "↑", "→", "←", "↑"], timeMs: 8000, context: "The designer leads a 'creative body exercise'!" },
  { sequence: ["→", "↑", "←", "↓", "→"], timeMs: 7000, context: "Meeting warm-up: the team lead sets the beat!" },
  { sequence: ["↑", "→", "↓", "←", "↑", "→", "↓"], timeMs: 9000, context: "Agile fitness: sprint exercise in the conference room!" },
  { sequence: ["←", "←", "→", "→", "↑", "↑", "↓"], timeMs: 9000, context: "The intern has brought along a TikTok routine!" },
  { sequence: ["↑", "↓", "↑", "↓", "↑", "↓"], timeMs: 7000, context: "Up-down challenge: who is fastest?" },
  { sequence: ["→", "↑", "→", "↑", "→"], timeMs: 7000, context: "Stair-climbing simulation, seated!" },
  { sequence: ["↓", "→", "↑", "←", "↓", "→", "↑", "←"], timeMs: 10000, context: "A full rotation — twice! Careful, this gets dizzy!" },
  { sequence: ["↑", "↑", "→", "→", "↓", "↓"], timeMs: 8000, context: "Stair challenge: up, up, right, right, down, down!" },
  { sequence: ["←", "↑", "→", "↓"], timeMs: 5000, context: "The boss runs the warm-up round. Short and painless!" },
  { sequence: ["↓", "↑", "↓", "↑", "→", "←", "→"], timeMs: 9000, context: "The Scrum Master has invented a 'movement kata'!" },
  { sequence: ["→", "→", "→", "←", "←", "←", "↑"], timeMs: 8000, context: "Sideways shuffle in the conference room! Everyone joins in!" },
  { sequence: ["↑", "←", "↑", "←", "↑", "←"], timeMs: 7000, context: "Diagonal sprint to the whiteboard! Without tripping!" },
  { sequence: ["↓", "↓", "↓", "↑", "↑", "↑", "↓", "↑"], timeMs: 10000, context: "Squat marathon: the fitness guru from HR demonstrates!" },
  { sequence: ["→", "↑", "←", "↑", "→", "↑"], timeMs: 7000, context: "Zigzag to the printer — the journey is the destination!" },
  { sequence: ["←", "↓", "→", "↑", "←", "↓", "→"], timeMs: 9000, context: "The spiral challenge: who gets dizzy first?" },
  { sequence: ["↑", "→", "↑", "→", "↑", "→", "↑", "→"], timeMs: 10000, context: "Stair simulation: four floors up — but seated!" },
  { sequence: ["↓", "←", "↑", "→", "↓"], timeMs: 6000, context: "Quick turn: one full rotation plus a bonus duck!" },
  { sequence: ["↑", "↑", "↑", "↓"], timeMs: 5000, context: "Stand three times, sit once. Short and intense!" },
  { sequence: ["←", "→", "↑", "↓", "←", "→", "↑", "↓"], timeMs: 10000, context: "Double cross challenge! The Scrum Master is on a roll!" },
  { sequence: ["→", "↓", "←", "↑", "→", "↓"], timeMs: 7000, context: "Clockwise turn with an extra bend at the end!" },
  { sequence: ["↑", "←", "↓", "→", "↑", "←", "↓"], timeMs: 9000, context: "Anticlockwise challenge: the designer choreographs!" },
  { sequence: ["↓", "↓", "→", "→", "↑", "↑", "←", "←"], timeMs: 10000, context: "Dancing the square — head of department edition!" },
  { sequence: ["↑", "→", "↓", "↓", "←", "↑"], timeMs: 7000, context: "L-shaped movement: like a chess knight!" },
  { sequence: ["←", "←", "↑", "↑", "→", "→", "↓", "↓"], timeMs: 10000, context: "The quadrangle: marketing has christened it the 'team synergy walk'!" },
  { sequence: ["↑", "↓", "→", "←", "↑", "↓", "→", "←", "↑"], timeMs: 11000, context: "Chaos sequence: the intern rolled dice!" },
  { sequence: ["→", "→", "↑", "←", "←", "↓"], timeMs: 7000, context: "One continuous line, no lifting: right, up, left, down!" },
  { sequence: ["↑", "↑", "→", "↓", "↓", "←", "↑", "→", "↓"], timeMs: 11000, context: "The ultimate fitness course — the boss is watching!" },
  { sequence: ["↑", "↓", "↑", "↓"], timeMs: 6000, context: "The boss's AI avatar demonstrates the energiser. It stutters." },
  { sequence: ["→", "→", "←", "←", "↑"], timeMs: 7000, context: "AI-generated team choreography. Nobody understands it, everybody joins in." },
  { sequence: ["↓", "←", "↑", "→", "↓", "←"], timeMs: 8000, context: "The meeting bot proposes a movement break. Objection is futile." },
];

// ---- Buzzword bingo ----

export const BUZZWORD_BINGO_CHALLENGES: BuzzwordBingoChallenge[] = [
  {
    words: ["Synergy", "Pivot", "Disruption", "Alignment"],
    correctOrder: [2, 0, 3, 1],
    timeMs: 10000,
    context: "The boss calls buzzword bingo! Click the words in the order they are called!",
  },
  {
    words: ["Scaling", "Agility", "Deep Dive", "Low-Hanging Fruit"],
    correctOrder: [3, 1, 0, 2],
    timeMs: 10000,
    context: "Marketing is playing buzzword bingo! Quick — the right order!",
  },
  {
    words: ["Stakeholder", "KPI", "Roadmap", "MVP"],
    correctOrder: [1, 3, 0, 2],
    timeMs: 10000,
    context: "The PO plays buzzword bingo with the team! Pay attention!",
  },
  {
    words: ["Lean", "Sprint", "Backlog", "Scrum"],
    correctOrder: [3, 2, 0, 1],
    timeMs: 9000,
    context: "The Scrum Master is testing your agile vocabulary!",
  },
  {
    words: ["Onboarding", "Offboarding", "Retention", "Culture Fit"],
    correctOrder: [0, 3, 2, 1],
    timeMs: 10000,
    context: "HR runs a little quiz — buzzword bingo edition!",
  },
  {
    words: ["Cloud", "DevOps", "Microservice", "Container"],
    correctOrder: [2, 3, 0, 1],
    timeMs: 9000,
    context: "The backend dev wants to test your tech vocabulary!",
  },
  {
    words: ["ROI", "Conversion", "Touchpoint", "Branding"],
    correctOrder: [1, 3, 0, 2],
    timeMs: 10000,
    context: "The marketing guru calls buzzwords — match the order!",
  },
  {
    words: ["Benchmark", "Pipeline", "Iteration", "Velocity"],
    correctOrder: [3, 0, 2, 1],
    timeMs: 10000,
    context: "Team meeting: who knows the buzzwords best?",
  },
  {
    words: ["Enabler", "Leverage", "Silo", "Transparency"],
    correctOrder: [2, 0, 3, 1],
    timeMs: 10000,
    context: "Management meeting! Buzzword bingo is running!",
  },
  {
    words: ["Impact", "Ownership", "Empower", "Scale"],
    correctOrder: [1, 3, 0, 2],
    timeMs: 10000,
    context: "The boss is firing off buzzwords — follow the order!",
  },
  {
    words: ["Ecosystem", "Deliverable", "Mindset", "Framework"],
    correctOrder: [3, 0, 2, 1],
    timeMs: 10000,
    context: "Innovation meeting: buzzword alert!",
  },
  {
    words: ["Bandwidth", "Circle Back", "Drill Down", "Synergy"],
    correctOrder: [0, 3, 1, 2],
    timeMs: 10000,
    context: "The client manager speaks exclusively in buzzwords!",
  },
  {
    words: ["Value Stream", "Epic", "Artifact", "Increment"],
    correctOrder: [1, 3, 0, 2],
    timeMs: 9000,
    context: "Agile vocabulary challenge: are you sharp?",
  },
  {
    words: ["North Star", "Moonshot", "Game Changer", "Bleeding Edge"],
    correctOrder: [2, 0, 3, 1],
    timeMs: 10000,
    context: "Strategy offsite: buzzword downpour!",
  },
  {
    words: ["Holistic", "Proactive", "Granular", "Actionable"],
    correctOrder: [3, 1, 0, 2],
    timeMs: 10000,
    context: "HR meeting: how many buzzwords do you know?",
  },
  {
    words: ["Best Practice", "Pain Point", "Quick Win", "Low Hanging Fruit"],
    correctOrder: [1, 3, 0, 2],
    timeMs: 10000,
    context: "Consulting bingo: every classic represented!",
  },
  {
    words: ["Bootstrapping", "Greenfield", "Brownfield", "Refactoring"],
    correctOrder: [0, 2, 1, 3],
    timeMs: 9000,
    context: "Tech meeting: developer buzzword edition!",
  },
  {
    words: ["Upskilling", "Cross-Functional", "Talent Pool", "Culture Add"],
    correctOrder: [2, 0, 3, 1],
    timeMs: 10000,
    context: "HR offsite: buzzword bingo on steroids!",
  },
  {
    words: ["OKR", "North Star Metric", "Pivot", "Product-Market Fit"],
    correctOrder: [3, 1, 0, 2],
    timeMs: 10000,
    context: "Startup buzzword challenge: do you know them all?",
  },
  {
    words: ["GitOps", "IaC", "Observability", "Zero Trust"],
    correctOrder: [2, 0, 3, 1],
    timeMs: 9000,
    context: "DevOps meeting: technical buzzwords incoming!",
  },
  {
    words: ["Kanban", "Definition of Done", "Blocker", "Impediment"],
    correctOrder: [0, 2, 3, 1],
    timeMs: 9000,
    context: "Daily standup: the Scrum Master is testing you!",
  },
  {
    words: ["Blue Ocean", "Red Ocean", "Disruption", "First Mover"],
    correctOrder: [1, 0, 3, 2],
    timeMs: 10000,
    context: "Strategy meeting: buzzwords of the highest order!",
  },
  {
    words: ["Scalable", "Modular", "Resilient", "Cloud-Native"],
    correctOrder: [3, 0, 2, 1],
    timeMs: 9000,
    context: "Architecture review: buzzword parade!",
  },
  {
    words: ["Omnichannel", "Persona", "Engagement", "Conversion"],
    correctOrder: [1, 3, 0, 2],
    timeMs: 10000,
    context: "Marketing standup: buzzword fireworks!",
  },
  {
    words: ["Retrospective", "Sprint Goal", "Velocity", "Epic"],
    correctOrder: [2, 3, 0, 1],
    timeMs: 9000,
    context: "Agile coach challenge: show what you know!",
  },
  {
    words: ["SLA", "Uptime", "Latency", "Throughput"],
    correctOrder: [0, 2, 3, 1],
    timeMs: 9000,
    context: "Operations meeting: performance buzzwords!",
  },
  {
    words: ["Growth Hacking", "Viral Loop", "Churn Rate", "CAC"],
    correctOrder: [3, 0, 2, 1],
    timeMs: 10000,
    context: "Growth team: do you know the metrics buzzwords?",
  },
  {
    words: ["Edge Computing", "Digital Twin", "Metaverse", "Web3"],
    correctOrder: [1, 3, 0, 2],
    timeMs: 10000,
    context: "Innovation lab: future buzzwords ahoy!",
  },
  {
    words: ["AI-Powered", "Prompt", "Hallucination", "Agent"],
    correctOrder: [3, 0, 2, 1],
    timeMs: 10000,
    context: "The boss explains the AI strategy. Four buzzwords in two minutes — bingo!",
  },
  {
    words: ["Copilot", "Guardrails", "Use Case", "Human-in-the-Loop"],
    correctOrder: [2, 3, 0, 1],
    timeMs: 10000,
    context: "The AI roadmap is presented. Memorise the order!",
  },
];

// ---- Sorting emails ----

export const EMAIL_SORTIEREN_CHALLENGES: EmailSortierenChallenge[] = [
  {
    emails: [
      { subject: "URGENT: server has stopped responding!", correct: "wichtig" },
      { subject: "You have won €10,000! Click now!", correct: "spam" },
      { subject: "Please restock the copier paper in the store room", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Your inbox is overflowing! Sort the mail at speed!",
  },
  {
    emails: [
      { subject: "RE: RE: RE: RE: FW: funny cat pictures", correct: "spam" },
      { subject: "Client XYZ is threatening to terminate immediately!", correct: "wichtig" },
      { subject: "Please replace the towels in the bathroom", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "A hundred unread mails! Sort them before the boss arrives!",
  },
  {
    emails: [
      { subject: "Newsletter: '7 ways to be a better manager' (issue 347)", correct: "spam" },
      { subject: "Security vulnerability discovered in production!", correct: "wichtig" },
      { subject: "Who is organising the cake for Lisa's birthday?", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Your inbox is detonating! Sort fast!",
  },
  {
    emails: [
      { subject: "Free webinar: 'Blockchain revolutionises EVERYTHING!'", correct: "spam" },
      { subject: "Board presentation tomorrow at 9 — your part is missing!", correct: "wichtig" },
      { subject: "Can somebody descale the coffee machine?", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "The inbox does not wait! Sort everything correctly!",
  },
  {
    emails: [
      { subject: "Amazon: your order (you ordered nothing)", correct: "spam" },
      { subject: "Deployment FAILED — immediate rollback required!", correct: "wichtig" },
      { subject: "Please update your photo for the intranet profile", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Monday morning, 47 new mails. Here we go!",
  },
  {
    emails: [
      { subject: "Compliance audit on Monday — documents missing!", correct: "wichtig" },
      { subject: "Free pizza!! Click HERE for your order!", correct: "spam" },
      { subject: "Book meeting room, third floor, Friday at 2pm", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Inbox chaos! Who sorts fastest?",
  },
  {
    emails: [
      { subject: "WARNING: database capacity at 98%!", correct: "wichtig" },
      { subject: "Lose weight in your sleep — doctors hate this trick!", correct: "spam" },
      { subject: "The office plants need watering", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Inbox alarm! Sort before it overflows!",
  },
  {
    emails: [
      { subject: "The board wants an immediate statement on the incident!", correct: "wichtig" },
      { subject: "FW: FW: FW: funny office memes 2026 😂😂😂", correct: "spam" },
      { subject: "Change the printer toner on floor 2", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "New mail! Sort fast, meeting in five minutes!",
  },
  {
    emails: [
      { subject: "Data breach reported — act immediately!", correct: "wichtig" },
      { subject: "Prize draw: iPad Pro FREE — today only!", correct: "spam" },
      { subject: "Who is ordering new whiteboard supplies?", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Your inbox is calling for help! Rescue it!",
  },
  {
    emails: [
      { subject: "Production outage at plant 3 — CEO informed!", correct: "wichtig" },
      { subject: "Crypto insider tip: 1000% returns guaranteed!!!", correct: "spam" },
      { subject: "Organise a birthday present for Herr Müller", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Forty-seven mails and rising. Go on, sort!",
  },
  {
    emails: [
      { subject: "Major client cancels contract — escalation!", correct: "wichtig" },
      { subject: "LinkedIn: 37 people viewed your profile", correct: "spam" },
      { subject: "Update the parking signs in the courtyard", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Mail upon mail! Who can still see the wood for the trees?",
  },
  {
    emails: [
      { subject: "Firewall breach at 03:42 — check the logs!", correct: "wichtig" },
      { subject: "You've been nominated for the 'Best Employee Award' (phishing)", correct: "spam" },
      { subject: "Please prepare visitor passes for Wednesday", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Monday morning chaos! Sort through it quickly!",
  },
  {
    emails: [
      { subject: "Legal: the deadline expires TODAY at 5pm!", correct: "wichtig" },
      { subject: "Exclusive offer: LED-illuminated necktie 🔥", correct: "spam" },
      { subject: "The kitchen fridge needs cleaning out", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "The inbox is a minefield! Sort carefully!",
  },
  {
    emails: [
      { subject: "Migration to the new ERP system is failing — help!", correct: "wichtig" },
      { subject: "RE: RE: why pineapple on pizza is wrong (thread)", correct: "spam" },
      { subject: "Order new business cards for the sales team", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "The inbox is overflowing — act fast!",
  },
  {
    emails: [
      { subject: "The auditor is arriving tomorrow, unannounced!", correct: "wichtig" },
      { subject: "Your horoscope this week: 'Big changes ahead!'", correct: "spam" },
      { subject: "Reorder flowers for the reception area", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Inbox tsunami! Sort your way through the flood!",
  },
  {
    emails: [
      { subject: "SSL certificate expires in two hours!", correct: "wichtig" },
      { subject: "Survey: which office dog type are you? 🐕", correct: "spam" },
      { subject: "Refill the hand soap in the toilets", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Sort quickly before the screen overflows!",
  },
  {
    emails: [
      { subject: "Investor call in 30 minutes — the deck is incomplete!", correct: "wichtig" },
      { subject: "A Nigerian prince would like to transfer $5m", correct: "spam" },
      { subject: "Please print name badges for the new interns", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "The inbox is on fire! Put it out!",
  },
  {
    emails: [
      { subject: "Client reports data loss — incident opened!", correct: "wichtig" },
      { subject: "FW: funny fail videos for your lunch break", correct: "spam" },
      { subject: "Who is clearing the conference room after the team event?", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Seventy-three unread mails after the holiday. Hold the line!",
  },
  {
    emails: [
      { subject: "The works council demands an immediate statement!", correct: "wichtig" },
      { subject: "Exclusive: 90% off motivational posters!", correct: "spam" },
      { subject: "Please book the company car in for its service", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Mail avalanche! Sort yourself free!",
  },
  {
    emails: [
      { subject: "CRITICAL: API key in a public repo!", correct: "wichtig" },
      { subject: "Test your biological age NOW — for free!", correct: "spam" },
      { subject: "Coordinate the ergonomics check for the workstations", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Inbox apocalypse! Save what can be saved!",
  },
  {
    emails: [
      { subject: "The landlord is terminating the office lease — two weeks' notice!", correct: "wichtig" },
      { subject: "Newsletter: the ten best office chairs of 2026", correct: "spam" },
      { subject: "Collect the old monitors for the charity drive", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Mail marathon! On your marks, get set, sort!",
  },
  {
    emails: [
      { subject: "Salary payment failed — contact HR!", correct: "wichtig" },
      { subject: "Your forgotten parcel is waiting! (tracking: XXX)", correct: "spam" },
      { subject: "Please organise a team photo for the Christmas card", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Friday afternoon, one last act of duty: sort the mail!",
  },
  {
    emails: [
      { subject: "Ransomware warning from the BSI — check the systems!", correct: "wichtig" },
      { subject: "WhatsApp Gold: upgrade now for premium features!", correct: "spam" },
      { subject: "Programme the key cards for the new starters", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Your inbox is running a fever! Treat it quickly!",
  },
  {
    emails: [
      { subject: "Supplier files for insolvency — supply chain at risk!", correct: "wichtig" },
      { subject: "URGENT: your PayPal account has been suspended (it hasn't)", correct: "spam" },
      { subject: "Empty the dishwasher in the kitchen", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Inbox under pressure! Sort against the clock!",
  },
  {
    emails: [
      { subject: "Emergency patch required for the production system!", correct: "wichtig" },
      { subject: "Free e-book: 'Succeed through manifesting'", correct: "spam" },
      { subject: "Who is organising drinks for the workshop?", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "The flood of email does not stop! Keep going!",
  },
  {
    emails: [
      { subject: "The draft contract must be signed by 12 noon!", correct: "wichtig" },
      { subject: "Apple gift card: you have been selected! 🎁", correct: "spam" },
      { subject: "Please write up the minutes from the last team meeting", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Twenty mails to inbox zero! You can do this!",
  },
  {
    emails: [
      { subject: "Power cut in the data centre — emergency operation!", correct: "wichtig" },
      { subject: "Personality test: which meeting type are you?", correct: "spam" },
      { subject: "The window in conference room B is stuck — call the caretaker", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "The inbox is bursting! Rescue your productivity!",
  },
  {
    emails: [
      { subject: "Regulation: new GDPR requirement effective immediately!", correct: "wichtig" },
      { subject: "Extend your warranty NOW — last chance!", correct: "spam" },
      { subject: "Reorder the department's coffee supply", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Mail jungle! Find your way through!",
  },
  {
    emails: [
      { subject: "Security incident: unauthorised access to the server room!", correct: "wichtig" },
      { subject: "Your DNA test says: you are 2% Viking! 🧬", correct: "spam" },
      { subject: "Arrange for the car park markings to be repainted", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Inbox chaos! Only you can restore order!",
  },
  {
    emails: [
      { subject: "Budget freeze — all spending above €500 needs approval!", correct: "wichtig" },
      { subject: "RE: FW: the best office pranks (with video!)", correct: "spam" },
      { subject: "Please schedule the fire safety briefing for the department", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "The inbox shows no mercy! Sort on bravely!",
  },
  {
    emails: [
      { subject: "Your AI agent answered 4,812 mails overnight", correct: "wichtig" },
      { subject: "DOUBLE your productivity with AI!!! Book now!", correct: "spam" },
      { subject: "Please link the new AI policy in the wiki", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "Your AI assistant pre-sorted this. Incorrectly.",
  },
  {
    emails: [
      { subject: "Security alert: API key found in a public repo", correct: "wichtig" },
      { subject: "Your prompt has been selected! Click here for your prize", correct: "spam" },
      { subject: "Who actually maintains our prompt collection?", correct: "delegieren" },
    ],
    timeMs: 12000,
    context: "The inbox after the AI rollout. It did not get better.",
  },
];

// ---- Calendar Tetris ----

export const KALENDER_TETRIS_CHALLENGES: KalenderTetrisChallenge[] = [
  {
    participants: [
      { name: "Klaus", slots: [true, false, true] },
      { name: "Sabine", slots: [true, false, false] },
      { name: "Jürgen", slots: [false, false, true] },
      { name: "Monika", slots: [true, false, true] },
    ],
    timeSlots: ["09:00", "11:00", "14:00"],
    correctSlot: 1,
    timeMs: 10000,
    context: "A meeting has to be scheduled! When is everyone free?",
  },
  {
    participants: [
      { name: "Bernd", slots: [false, true, false] },
      { name: "Heike", slots: [true, false, false] },
      { name: "Detlef", slots: [false, true, false] },
      { name: "Gisela", slots: [true, true, false] },
    ],
    timeSlots: ["10:00", "13:00", "15:00"],
    correctSlot: 2,
    timeMs: 10000,
    context: "A 1-on-1 needs booking urgently! Which slot is free?",
  },
  {
    participants: [
      { name: "Torsten", slots: [true, false, true, false] },
      { name: "Ute", slots: [false, false, false, true] },
      { name: "Herbert", slots: [true, false, false, true] },
      { name: "Petra", slots: [false, false, true, false] },
    ],
    timeSlots: ["08:00", "10:00", "13:00", "16:00"],
    correctSlot: 1,
    timeMs: 12000,
    context: "Four slots, four people — when does it work for everyone?",
  },
  {
    participants: [
      { name: "Rainer", slots: [true, false, true] },
      { name: "Gudrun", slots: [false, false, true] },
      { name: "Achim", slots: [true, false, false] },
      { name: "Bärbel", slots: [true, false, true] },
      { name: "Sven", slots: [false, false, true] },
    ],
    timeSlots: ["09:00", "12:00", "15:00"],
    correctSlot: 1,
    timeMs: 11000,
    context: "Five attendees to check — find the free slot fast!",
  },
  {
    participants: [
      { name: "Kerstin", slots: [true, false, false, true] },
      { name: "Olaf", slots: [false, false, true, false] },
      { name: "Birgit", slots: [true, false, false, true] },
      { name: "Norbert", slots: [false, false, true, false] },
    ],
    timeSlots: ["08:30", "10:30", "13:30", "15:30"],
    correctSlot: 1,
    timeMs: 11000,
    context: "The boss wants to know: when can everyone make it?",
  },
  {
    participants: [
      { name: "Elke", slots: [false, true, false] },
      { name: "Hartmut", slots: [false, false, true] },
      { name: "Renate", slots: [false, true, true] },
      { name: "Uwe", slots: [false, true, false] },
      { name: "Margit", slots: [false, true, true] },
    ],
    timeSlots: ["09:00", "11:00", "14:00"],
    correctSlot: 0,
    timeMs: 11000,
    context: "Get the sprint review booked — which slot suits everyone?",
  },
  {
    participants: [
      { name: "Dieter", slots: [false, true, true, false] },
      { name: "Ingrid", slots: [true, false, false, false] },
      { name: "Kevin", slots: [false, true, true, false] },
      { name: "Chantal", slots: [true, false, false, false] },
    ],
    timeSlots: ["09:00", "11:00", "14:00", "16:00"],
    correctSlot: 3,
    timeMs: 11000,
    context: "The calendar is a minefield! Which slot is free?",
  },
  {
    participants: [
      { name: "Justin", slots: [true, true, false] },
      { name: "Jessica", slots: [false, true, false] },
      { name: "Malte", slots: [false, true, false] },
      { name: "Svenja", slots: [true, false, false] },
    ],
    timeSlots: ["10:00", "13:00", "16:00"],
    correctSlot: 2,
    timeMs: 10000,
    context: "Squeeze in a retro — when is everybody free?",
  },
  {
    participants: [
      { name: "Bernd", slots: [true, false, false, true] },
      { name: "Sabine", slots: [false, false, true, false] },
      { name: "Klaus", slots: [true, false, false, true] },
      { name: "Heike", slots: [false, false, true, true] },
      { name: "Torsten", slots: [true, false, false, false] },
    ],
    timeSlots: ["08:00", "10:00", "14:00", "16:00"],
    correctSlot: 1,
    timeMs: 12000,
    context: "Five colleagues, four slots — calendar chaos!",
  },
  {
    participants: [
      { name: "Monika", slots: [true, false, true] },
      { name: "Detlef", slots: [false, false, true] },
      { name: "Gisela", slots: [true, false, false] },
      { name: "Rainer", slots: [true, false, true] },
    ],
    timeSlots: ["09:30", "12:00", "15:00"],
    correctSlot: 1,
    timeMs: 10000,
    context: "A planning meeting is needed! When is the calendar clear?",
  },
  {
    participants: [
      { name: "Gudrun", slots: [false, true, true, false] },
      { name: "Achim", slots: [true, false, true, false] },
      { name: "Bärbel", slots: [false, true, false, false] },
      { name: "Sven", slots: [true, false, true, false] },
    ],
    timeSlots: ["08:00", "10:00", "13:00", "15:00"],
    correctSlot: 3,
    timeMs: 11000,
    context: "When is there a window open for everyone?",
  },
  {
    participants: [
      { name: "Kerstin", slots: [true, false, true] },
      { name: "Olaf", slots: [false, false, true] },
      { name: "Norbert", slots: [true, false, false] },
      { name: "Birgit", slots: [false, false, true] },
      { name: "Elke", slots: [true, false, false] },
    ],
    timeSlots: ["09:00", "11:00", "14:00"],
    correctSlot: 1,
    timeMs: 11000,
    context: "Quickly — the boss is waiting for the invite!",
  },
  {
    participants: [
      { name: "Hartmut", slots: [false, true, false, true] },
      { name: "Renate", slots: [false, false, true, false] },
      { name: "Uwe", slots: [false, true, false, true] },
      { name: "Margit", slots: [false, false, true, true] },
    ],
    timeSlots: ["09:00", "11:00", "14:00", "16:00"],
    correctSlot: 0,
    timeMs: 11000,
    context: "Which slot is free for all of them? Scan fast!",
  },
  {
    participants: [
      { name: "Dieter", slots: [false, true, true, false] },
      { name: "Ingrid", slots: [true, false, true, false] },
      { name: "Kevin", slots: [false, true, false, false] },
      { name: "Chantal", slots: [true, true, false, false] },
      { name: "Justin", slots: [false, false, true, false] },
    ],
    timeSlots: ["08:30", "10:30", "13:30", "15:30"],
    correctSlot: 3,
    timeMs: 12000,
    context: "Five people, four slots — the calendar nightmare!",
  },
  {
    participants: [
      { name: "Jessica", slots: [true, false, true] },
      { name: "Malte", slots: [true, false, false] },
      { name: "Svenja", slots: [false, false, true] },
      { name: "Klaus", slots: [true, false, true] },
    ],
    timeSlots: ["10:00", "12:00", "15:00"],
    correctSlot: 1,
    timeMs: 10000,
    context: "Schedule the brainstorming — which slot fits?",
  },
  {
    participants: [
      { name: "Sabine", slots: [false, true, false, true] },
      { name: "Bernd", slots: [true, false, false, true] },
      { name: "Heike", slots: [false, true, false, false] },
      { name: "Detlef", slots: [true, false, false, true] },
    ],
    timeSlots: ["09:00", "11:00", "14:00", "16:00"],
    correctSlot: 2,
    timeMs: 11000,
    context: "Find a workshop date! When does it suit everyone?",
  },
  {
    participants: [
      { name: "Gisela", slots: [true, false, true] },
      { name: "Torsten", slots: [false, false, true] },
      { name: "Ute", slots: [true, false, false] },
      { name: "Herbert", slots: [false, false, true] },
      { name: "Petra", slots: [true, false, false] },
    ],
    timeSlots: ["09:00", "12:00", "15:00"],
    correctSlot: 1,
    timeMs: 11000,
    context: "Departmental meeting — find the common slot!",
  },
  {
    participants: [
      { name: "Rainer", slots: [true, false, true, false] },
      { name: "Gudrun", slots: [false, false, false, true] },
      { name: "Achim", slots: [false, false, true, false] },
      { name: "Bärbel", slots: [true, false, true, true] },
    ],
    timeSlots: ["08:00", "10:00", "13:00", "16:00"],
    correctSlot: 1,
    timeMs: 11000,
    context: "Escalation meeting! When is everybody available?",
  },
  {
    participants: [
      { name: "Sven", slots: [false, true, false] },
      { name: "Kerstin", slots: [true, true, false] },
      { name: "Olaf", slots: [false, false, false] },
      { name: "Birgit", slots: [true, true, false] },
      { name: "Norbert", slots: [false, true, false] },
    ],
    timeSlots: ["10:00", "13:00", "16:00"],
    correctSlot: 2,
    timeMs: 11000,
    context: "Schedule the client meeting — which slot works for all?",
  },
  {
    participants: [
      { name: "Elke", slots: [false, true, false, true] },
      { name: "Hartmut", slots: [false, false, true, false] },
      { name: "Renate", slots: [false, true, false, true] },
      { name: "Uwe", slots: [false, true, true, false] },
    ],
    timeSlots: ["09:00", "11:00", "14:00", "16:00"],
    correctSlot: 0,
    timeMs: 11000,
    context: "Board presentation — and the calendar is packed!",
  },
  {
    participants: [
      { name: "Margit", slots: [true, true, false, true] },
      { name: "Dieter", slots: [false, true, false, false] },
      { name: "Ingrid", slots: [false, false, false, true] },
      { name: "Kevin", slots: [true, false, false, false] },
      { name: "Chantal", slots: [false, true, false, true] },
    ],
    timeSlots: ["08:00", "10:00", "13:00", "15:00"],
    correctSlot: 2,
    timeMs: 12000,
    context: "Five attendees, four slots — maximum chaos!",
  },
  {
    participants: [
      { name: "Justin", slots: [true, false, true] },
      { name: "Jessica", slots: [false, false, true] },
      { name: "Malte", slots: [true, false, false] },
      { name: "Svenja", slots: [false, false, true] },
    ],
    timeSlots: ["09:30", "12:30", "15:30"],
    correctSlot: 1,
    timeMs: 10000,
    context: "Squeeze in the Jour Fixe — when is everything free?",
  },
  {
    participants: [
      { name: "Klaus", slots: [true, false, true, false] },
      { name: "Sabine", slots: [false, false, false, true] },
      { name: "Bernd", slots: [true, false, false, false] },
      { name: "Monika", slots: [false, false, true, true] },
    ],
    timeSlots: ["08:30", "11:00", "13:30", "16:00"],
    correctSlot: 1,
    timeMs: 11000,
    context: "A refinement slot is needed! Which one suits everyone?",
  },
  {
    participants: [
      { name: "Heike", slots: [false, true, true] },
      { name: "Detlef", slots: [false, false, true] },
      { name: "Gisela", slots: [false, true, true] },
      { name: "Torsten", slots: [false, true, false] },
      { name: "Ute", slots: [false, true, true] },
    ],
    timeSlots: ["10:00", "13:00", "15:00"],
    correctSlot: 0,
    timeMs: 11000,
    context: "Demo date — when can all five make it?",
  },
  {
    participants: [
      { name: "Herbert", slots: [false, true, false, true] },
      { name: "Petra", slots: [false, false, true, true] },
      { name: "Rainer", slots: [false, true, false, false] },
      { name: "Gudrun", slots: [false, false, true, true] },
    ],
    timeSlots: ["09:00", "11:00", "14:00", "16:00"],
    correctSlot: 0,
    timeMs: 11000,
    context: "All-day workshop — when does it really suit everyone?",
  },
  {
    participants: [
      { name: "Achim", slots: [true, false, true] },
      { name: "Bärbel", slots: [false, false, true] },
      { name: "Sven", slots: [true, false, false] },
      { name: "Kerstin", slots: [true, false, true] },
      { name: "Olaf", slots: [false, false, true] },
    ],
    timeSlots: ["08:00", "12:00", "16:00"],
    correctSlot: 1,
    timeMs: 11000,
    context: "The Scrum Master needs a slot RIGHT NOW!",
  },
  {
    participants: [
      { name: "Birgit", slots: [false, true, true, false] },
      { name: "Norbert", slots: [true, false, true, false] },
      { name: "Elke", slots: [false, true, false, false] },
      { name: "Hartmut", slots: [true, false, true, false] },
    ],
    timeSlots: ["09:00", "10:30", "13:00", "15:30"],
    correctSlot: 3,
    timeMs: 11000,
    context: "Compliance meeting — which slot is free for everyone?",
  },
  {
    participants: [
      { name: "Renate", slots: [true, false, true] },
      { name: "Uwe", slots: [false, false, true] },
      { name: "Margit", slots: [true, false, false] },
      { name: "Dieter", slots: [false, false, true] },
    ],
    timeSlots: ["09:00", "11:00", "14:00"],
    correctSlot: 1,
    timeMs: 10000,
    context: "Book a quick sync — when does it suit all of them?",
  },
  {
    participants: [
      { name: "Ingrid", slots: [true, false, false, true] },
      { name: "Kevin", slots: [false, false, true, false] },
      { name: "Chantal", slots: [true, false, false, true] },
      { name: "Justin", slots: [false, false, true, false] },
      { name: "Jessica", slots: [true, false, false, true] },
    ],
    timeSlots: ["08:00", "10:00", "13:00", "16:00"],
    correctSlot: 1,
    timeMs: 12000,
    context: "Organise the town hall — which slot is conflict-free?",
  },
  {
    participants: [
      { name: "Malte", slots: [false, true, true, false] },
      { name: "Svenja", slots: [true, false, true, false] },
      { name: "Klaus", slots: [false, true, false, false] },
      { name: "Sabine", slots: [true, false, true, false] },
    ],
    timeSlots: ["09:00", "11:30", "14:00", "16:30"],
    correctSlot: 3,
    timeMs: 11000,
    context: "Strategy workshop — when is everyone completely free?",
  },
  {
    participants: [
      { name: "Marcel", slots: [true, true, false, true] },
      { name: "Nadja", slots: [false, true, false, true] },
      { name: "Ingo", slots: [true, false, false, true] },
    ],
    timeSlots: ["09:00", "11:00", "13:00", "16:00"],
    correctSlot: 2,
    timeMs: 10000,
    context: "The AI assistant proposed three slots. All of them clash.",
  },
];

// ---- Code review ----

export const CODE_REVIEW_CHALLENGES: CodeReviewChallenge[] = [
  {
    code: "if (user.role = 'admin') {\n  grantAccess();\n}",
    options: ["== instead of =", "=== instead of =", "!= instead of ="],
    correctIndex: 1,
    language: "JavaScript",
    timeMs: 10000,
    context: "Code review! Find the bug in the auth code!",
  },
  {
    code: "for (let i = 0; i <= arr.length; i++) {\n  process(arr[i]);\n}",
    options: ["i < arr.length", "i >= arr.length", "i != arr.length"],
    correctIndex: 0,
    language: "TypeScript",
    timeMs: 10000,
    context: "Off-by-one error? The classic!",
  },
  {
    code: "const data = await fetch('/api');\nconst json = data.json();",
    options: ["await data.json()", "data.text()", "JSON.parse(data)"],
    correctIndex: 0,
    language: "JavaScript",
    timeMs: 10000,
    context: "Async/await bug — can you spot it?",
  },
  {
    code: "SELECT * FROM users\nWHERE name = 'admin'\nOR 1=1; --'",
    options: ["Use prepared statements", "Just remove the OR", "Drop the WHERE clause"],
    correctIndex: 0,
    language: "SQL",
    timeMs: 10000,
    context: "SQL injection! How do you fix it?",
  },
  {
    code: "const timer = setInterval(() => {\n  update();\n}, 1000);\n// component gets destroyed",
    options: ["clearInterval(timer) on cleanup", "setTimeout instead of setInterval", "Call update() directly"],
    correctIndex: 0,
    language: "TypeScript",
    timeMs: 10000,
    context: "Memory leak! The timer runs forever!",
  },
  {
    code: "password = md5(userInput);",
    options: ["Use SHA-256", "Use bcrypt/argon2 with a salt", "Base64 instead of md5"],
    correctIndex: 1,
    language: "Python",
    timeMs: 10000,
    context: "Security review! The password hashing is unsafe!",
  },
  {
    code: "console.log(0.1 + 0.2 == 0.3);\n// result: false",
    options: ["Use ===", "Check Math.abs(a-b) < epsilon", "Use parseInt"],
    correctIndex: 1,
    language: "JavaScript",
    timeMs: 10000,
    context: "Floating-point bug! How do you compare correctly?",
  },
  {
    code: "try {\n  riskyOperation();\n} catch (e) {\n  // TODO: handle error\n}",
    options: ["Add throw e;", "Log the error and handle it properly", "Remove the try-catch"],
    correctIndex: 1,
    language: "TypeScript",
    timeMs: 10000,
    context: "Empty catch block! Errors are being swallowed!",
  },
  {
    code: "const apiKey = 'sk-1234abcd';\nfetch('/api', { headers: { key: apiKey }});",
    options: ["Move the API key into an environment variable", "Base64-encode the API key", "Put the API key in a comment"],
    correctIndex: 0,
    language: "TypeScript",
    timeMs: 10000,
    context: "Hardcoded secret! The security scanner is going off!",
  },
  {
    code: "innerHTML = '<div>' + userInput + '</div>';",
    options: ["textContent instead of innerHTML", "Wrap userInput in quotes", "encodeURI(userInput)"],
    correctIndex: 0,
    language: "JavaScript",
    timeMs: 10000,
    context: "XSS hole! User input is rendered straight through!",
  },
  {
    code: "if (arr.length > 0)\n  doFirst();\n  doSecond();",
    options: ["Braces around the block", "Semicolon after doFirst()", "Add an else"],
    correctIndex: 0,
    language: "JavaScript",
    timeMs: 10000,
    context: "Dangling statement! doSecond() always runs!",
  },
  {
    code: "const result = numbers.sort();\n// [1, 10, 2, 20, 3]",
    options: ["sort((a,b) => a - b)", "sort().reverse()", "sort(Number)"],
    correctIndex: 0,
    language: "JavaScript",
    timeMs: 10000,
    context: "Sort bug! Numbers are being sorted as strings!",
  },
  {
    code: "useEffect(() => {\n  setCount(count + 1);\n});",
    options: ["Add a dependency array []", "Use useCallback", "Remove count from state"],
    correctIndex: 0,
    language: "React",
    timeMs: 10000,
    context: "Infinite render loop! The component is crashing!",
  },
  {
    code: "const config = JSON.parse(fs.readFileSync('config.json'));\n// inside the request handler",
    options: ["Load the file once at startup and cache it", "Use JSON.stringify", "YAML instead of JSON"],
    correctIndex: 0,
    language: "Node.js",
    timeMs: 10000,
    context: "Performance bug! The file is read on every request!",
  },
  {
    code: "DELETE FROM orders\nWHERE status = 'cancelled'",
    options: ["Add a LIMIT or take a backup first", "A transaction with a ROLLBACK option", "UPDATE instead of DELETE"],
    correctIndex: 1,
    language: "SQL",
    timeMs: 10000,
    context: "A dangerous DELETE with no safety net!",
  },
  {
    code: "git push --force origin main",
    options: ["Use --force-with-lease", "Use --force-all", "git reset first"],
    correctIndex: 0,
    language: "Git",
    timeMs: 10000,
    context: "Force push to main! That is dangerous!",
  },
  {
    code: "while (true) {\n  const data = fetchData();\n  if (data) break;\n}",
    options: ["A sleep/delay between attempts", "Remove the timeout", "Cache fetchData()"],
    correctIndex: 0,
    language: "TypeScript",
    timeMs: 10000,
    context: "Busy wait! The CPU is spinning!",
  },
  {
    code: "const users = db.query(\n  `SELECT * FROM users WHERE id = ${userId}`\n);",
    options: ["A template string with escaping", "Use a parameterised query", "Wrap userId in parseInt"],
    correctIndex: 1,
    language: "Node.js",
    timeMs: 10000,
    context: "SQL injection through string interpolation!",
  },
  {
    code: "function getUser(id) {\n  return db.find(id);\n}\n// Returns undefined or User",
    options: ["Always return null", "Chain with optional ?", "Declare the return type explicitly and null-check"],
    correctIndex: 2,
    language: "TypeScript",
    timeMs: 10000,
    context: "Type safety! The function can return undefined!",
  },
  {
    code: "localStorage.setItem('token', jwt);\n// available on every page",
    options: ["Use sessionStorage", "An HttpOnly cookie instead of localStorage", "Put the token in a URL parameter"],
    correctIndex: 1,
    language: "JavaScript",
    timeMs: 10000,
    context: "JWT in localStorage — XSS risk!",
  },
  {
    code: "if (user && user.address && user.address.city)\n  return user.address.city;",
    options: ["Optional chaining: user?.address?.city", "Wrap it in try-catch", "user.address!.city"],
    correctIndex: 0,
    language: "TypeScript",
    timeMs: 10000,
    context: "A chain of null checks! There is something better!",
  },
  {
    code: "import moment from 'moment';\n// bundle: 67KB gzipped",
    options: ["Write your own date functions", "Use date-fns or dayjs", "Moment.js is perfect as it is"],
    correctIndex: 1,
    language: "JavaScript",
    timeMs: 10000,
    context: "Bundle size! Moment.js is enormous!",
  },
  {
    code: "res.send({ error: err.stack });",
    options: ["Send a generic error message", "err.message instead of stack", "console.log the error"],
    correctIndex: 0,
    language: "Express",
    timeMs: 10000,
    context: "Stack trace to the client! Information disclosure!",
  },
  {
    code: "const arr = [1, 2, 3];\ndelete arr[1];\n// [1, empty, 3]",
    options: ["Use arr.splice(1, 1)", "arr.filter instead of delete", "arr.splice(1, 1) or arr.filter"],
    correctIndex: 2,
    language: "JavaScript",
    timeMs: 10000,
    context: "Hole in the array! delete leaves empty slots!",
  },
  {
    code: "// .env\nDB_PASSWORD=admin123",
    options: ["Use a stronger password", "Use a secrets manager/vault", "Encrypt the password"],
    correctIndex: 1,
    language: "Config",
    timeMs: 10000,
    context: "Password in the .env! And it is admin123!",
  },
  {
    code: "app.use(cors({ origin: '*' }));",
    options: ["Whitelist specific origins", "Remove CORS entirely", "Add credentials: true"],
    correctIndex: 0,
    language: "Express",
    timeMs: 10000,
    context: "CORS wildcard! Every domain has access!",
  },
  {
    code: "new Date().getYear()\n// expected: 2026, result: 126",
    options: ["Use getFullYear()", "getYear() + 2000", "Date.now().year"],
    correctIndex: 0,
    language: "JavaScript",
    timeMs: 10000,
    context: "Date bug! getYear() returns years since 1900!",
  },
  {
    code: "setTimeout(doSomething(), 1000);",
    options: ["Pass doSomething without the parentheses", "A callback with clearTimeout", "setInterval instead of setTimeout"],
    correctIndex: 0,
    language: "JavaScript",
    timeMs: 10000,
    context: "The function runs immediately instead of after one second!",
  },
  {
    code: "const copy = originalArray;\ncopy.push('new');\n// originalArray changed too!",
    options: ["[...originalArray] or Array.from()", "copy = originalArray.slice()", "Either works — spread or slice"],
    correctIndex: 2,
    language: "JavaScript",
    timeMs: 10000,
    context: "Forgot the shallow copy! A reference, not a copy!",
  },
  {
    code: "if (status === 200 || 201) {\n  success();\n}",
    options: ["status === 200 || status === 201", "[200, 201].includes(status)", "Either works"],
    correctIndex: 2,
    language: "JavaScript",
    timeMs: 10000,
    context: "Logic error! 201 is always truthy!",
  },
  {
    code: "async function save() {\n  db.insert(data);\n  return 'saved';\n}",
    options: ["await db.insert(data)", "return db.insert(data)", "Add a try-catch"],
    correctIndex: 0,
    language: "TypeScript",
    timeMs: 10000,
    context: "Missing await! Nothing waits for the database!",
  },
  {
    code: "const el = document.getElementById('btn');\nel.addEventListener('click', handler);\n// el could be null",
    options: ["Null-check before addEventListener", "el! (non-null assertion)", "querySelector instead of getElementById"],
    correctIndex: 0,
    language: "TypeScript",
    timeMs: 10000,
    context: "Null access! getElementById can return null!",
  },
  {
    code: "for (var i = 0; i < 5; i++) {\n  setTimeout(() => log(i), 100);\n}\n// logs '5' five times",
    options: ["Use let instead of var", "Use const instead of var", "Pass i as a parameter"],
    correctIndex: 0,
    language: "JavaScript",
    timeMs: 10000,
    context: "Closure bug! var has no block scope!",
  },
  {
    code: "typeof null\n// result: 'object'",
    options: ["Check value === null", "typeof value !== 'object'", "Use isNull(value)"],
    correctIndex: 0,
    language: "JavaScript",
    timeMs: 10000,
    context: "typeof null is 'object'! A historic JS bug!",
  },
  {
    code: "git commit -m 'fix'\ngit commit -m 'fix2'\ngit commit -m 'fix fix'",
    options: ["Write meaningful commit messages", "Use git squash", "Both — squash and better messages"],
    correctIndex: 2,
    language: "Git",
    timeMs: 10000,
    context: "Commit history chaos! What changed here?",
  },
  {
    code: "const response = fetch('/api');\nif (response.ok) { ... }",
    options: ["await before fetch()", "Check response.status", "Use .then()"],
    correctIndex: 0,
    language: "JavaScript",
    timeMs: 10000,
    context: "Promise not resolved! fetch is async!",
  },
  {
    code: "// generated by the AI\nconst total = items.reduce((a, b) => a + b);",
    options: ["Add an initial value of 0", "map instead of reduce", "Remove the semicolon"],
    correctIndex: 0,
    language: "TypeScript",
    timeMs: 10000,
    context: "The AI wrote the reducer. On an empty list it explodes.",
  },
  {
    code: "const key = 'sk-prod-4f8a...';\nawait ai.complete(prompt, { key });",
    options: ["Load the key from the environment", "Rename the key", "Put the key in a comment"],
    correctIndex: 0,
    language: "TypeScript",
    timeMs: 10000,
    context: "The assistant helpfully pasted the API key in as well.",
  },
];

// ---- AI hallucinations ----
/**
 * The assistant wrote it, somebody has to read it.
 *
 * Every slide here is mostly true, which is the whole problem: the invented
 * lines are the ones that sound best. A run of these teaches exactly one
 * lesson, and it is the right one — the more flattering a claim is, the more
 * likely nobody checked it.
 */
export const KI_HALLUZINATION_CHALLENGES: KiHalluzinationChallenge[] = [
  {
    tool: "MeetGPT-5",
    task: "Summarise the sprint. Keep it positive.",
    statements: [
      "The team completed 47 of 52 story points.",
      "Velocity is up 12% on the previous quarter.",
      "Kevin finished the database migration single-handedly, overnight.",
      "Two tickets were carried into the next sprint.",
      "The team has been nominated for the German Innovation Award 2026.",
    ],
    hallucinations: [2, 4],
    timeMs: 15000,
    context: "The AI wrote the sprint report. Two of these sentences are invented.",
  },
  {
    tool: "Meeting Copilot",
    task: "Write up the minutes from the daily.",
    statements: [
      "Sandra reports progress on the backend.",
      "No blockers were raised.",
      "You committed to completing the migration on your own by Friday.",
      "The next daily takes place at 9:15 as usual.",
    ],
    hallucinations: [2],
    timeMs: 13000,
    context: "The AI minutes go out to everyone shortly. Read them before the boss does.",
  },
  {
    tool: "InsightAI Pro",
    task: "Analyse last quarter's customer feedback.",
    statements: [
      "Satisfaction sits at 3.8 out of 5.",
      "Loading time was the most frequent complaint.",
      "Müller GmbH has extended its contract by five years.",
      "14% of those surveyed did not respond at all.",
    ],
    hallucinations: [2],
    timeMs: 13000,
    context: "The analysis is finished. Whether it is true, nobody asked.",
  },
  {
    tool: "DeepMarket 3",
    task: "Produce a competitive analysis. One page.",
    statements: [
      "According to the study, the market grows 4% annually.",
      "Our main competitor filed for insolvency this year.",
      "Three new entrants have come into the market.",
      "The underlying study dates from 2019.",
      "Analyst ratings make us the market leader in our segment.",
    ],
    hallucinations: [1, 4],
    timeMs: 15000,
    context: "Market analysis generated in four seconds. If only.",
  },
  {
    tool: "Copilot Enterprise",
    task: "Document the auth class.",
    statements: [
      "The class validates tokens against the auth server.",
      "Internally it uses the authlib-turbo library.",
      "Expired tokens throw an AuthError.",
      "The refresh() method has been deprecated since version 2.1.",
    ],
    hallucinations: [1, 3],
    timeMs: 14000,
    context: "The docs are generated. Half the packages named actually exist.",
  },
  {
    tool: "Talentia HR",
    task: "Summarise the candidate's CV.",
    statements: [
      "Eight years of backend experience.",
      "Fluent in German and English.",
      "Sold his own startup to a US corporation in 2021.",
      "Changes jobs roughly every eighteen months.",
    ],
    hallucinations: [2],
    timeMs: 13000,
    context: "The AI screening report. The candidate is already waiting outside.",
  },
  {
    tool: "FinBot",
    task: "Prepare the quarterly figures for management.",
    statements: [
      "Revenue is 3% below plan.",
      "Staff costs have risen.",
      "The company has been debt-free since the second quarter.",
      "Q4 is expected to move sideways.",
    ],
    hallucinations: [2],
    timeMs: 13000,
    context: "Numbers from the AI. The CFO is about to read along.",
  },
  {
    tool: "StrategyGPT",
    task: "Build a roadmap. It has to look ambitious.",
    statements: [
      "Feature A is planned for Q1.",
      "Feature B still needs sign-off from data protection.",
      "The AI integration is already fully implemented.",
      "The launch date has not been fixed yet.",
      "The board approved the roadmap unanimously.",
    ],
    hallucinations: [2, 4],
    timeMs: 15000,
    context: "The roadmap came out of the prompt. So, unfortunately, did the commitments.",
  },
  {
    tool: "LearnBot",
    task: "Summarise the mandatory training.",
    statements: [
      "The training takes two hours.",
      "All staff have already completed it.",
      "It ends with a ten-question quiz.",
      "Attendance is compulsory.",
    ],
    hallucinations: [1],
    timeMs: 13000,
    context: "Compliance summary, AI-generated. What could possibly go wrong.",
  },
  {
    tool: "PipelineGPT",
    task: "Assess our pipeline for the board.",
    statements: [
      "There are 34 deals in the funnel.",
      "The average close rate is 18%.",
      "Three customers have already agreed verbally.",
      "Two deals have not moved in eight months.",
      "Sales have confirmed the forecast.",
    ],
    hallucinations: [2, 4],
    timeMs: 15000,
    context: "The AI assessed the pipeline. Optimistic does not begin to cover it.",
  },
  {
    tool: "OpsCopilot",
    task: "Write the post-mortem for the outage.",
    statements: [
      "The outage lasted 42 minutes.",
      "The cause was a faulty deployment.",
      "No customer noticed the outage.",
      "The rollback ran fully automatically.",
      "A follow-up ticket has been created.",
    ],
    hallucinations: [2, 3],
    timeMs: 15000,
    context: "The post-mortem is generated. It reads remarkably relaxed.",
  },
  {
    tool: "DocuBot",
    task: "Write the onboarding docs for new devs.",
    statements: [
      "Setup takes about half a day.",
      "The database runs locally in Docker.",
      "A single call to setup.sh configures everything.",
      "The team answers questions in chat.",
    ],
    hallucinations: [2],
    timeMs: 13000,
    context: "The onboarding docs are done. One of the scripts in them does not exist.",
  },
  {
    tool: "ESG Assistant",
    task: "Draft our sustainability report.",
    statements: [
      "Power consumption is down 6%.",
      "We have been offsetting all business travel since this year.",
      "The figures come from internal reporting.",
      "An external audit is still pending.",
      "We are already climate neutral.",
    ],
    hallucinations: [1, 4],
    timeMs: 15000,
    context: "The ESG report came out of a prompt. The auditors come later.",
  },
];

// ---- Flavour interjections during the meeting ----

export const RANDOM_MEETING_FLAVOR = [
  // Classics
  "Somebody has their camera on with a cat background.",
  "Somebody's mic was not muted. You can hear a vacuum cleaner.",
  "The projector does not work. Surprise.",
  "Somebody is typing audibly and aggressively.",
  "A fire engine wails outside. Nobody reacts.",
  "'Can you hear me? CAN YOU HEAR ME??' — Yes. We can.",
  "A colleague is eating crisps. Loudly.",
  "'Sorry, I was on mute' — for the fifth time today.",
  "The intern accidentally shares their screen. It is Reddit.",
  "'We should take this offline.' — What does that even mean?",
  "Somebody shared their Outlook calendar. Twelve meetings today.",
  "'Can we keep this short?' — Narrator: they could not.",
  "PowerPoint presentation: slide 1 of 87.",
  "The boss tells a joke. Polite laughter.",
  "A phone rings. It is the Macarena remix.",
  "Somebody dialled the wrong number. Three times.",
  "The coffee has run out. Panic spreads.",
  "Somebody is eating an apple. Microphone gain at maximum.",
  "A colleague is joining from the car. You can hear the indicator.",
  "The air conditioning kicks in. Arctic temperatures.",
  "Somebody prepared 47 slides. For a fifteen-minute meeting.",
  "The screen flickers. IT were informed. Three weeks ago.",
  "'That's a good point.' — Nobody knows which point.",
  "Somebody has locked themselves inside the conference room.",
  "The coffee machine is making threatening noises.",
  "'Can we park that?' — The meeting car park is full.",
  "Somebody booked the wrong room. Double occupancy.",
  "The printer suddenly prints 200 pages. Nobody knows why.",
  "A window is open. Traffic noise dominates.",
  "Somebody brought cake. The mood briefly lifts.",
  "A colleague's laptop goes to sleep. Mid-sentence.",
  "'Who is taking the minutes?' — Silence. Eyes on the intern.",
  "The Wi-Fi collapses. A brief mercy.",
  "Somebody uses the whiteboard. Every pen is dried out.",
  "A colleague takes a quiet call in the corner. Not quiet enough.",
  "'I have to be on my next call in five minutes.' — Everybody says this.",
  "A baby cries in the background of a remote attendee.",
  "Somebody brought their breakfast. It smells of tuna.",
  "The cleaner comes in and vacuums around the table.",
  "Somebody says 'synergy'. Three people groan in unison.",
  "The projector shows a blue screen. 'One moment, I'll fix it...'",
  "'I've got a hard stop at two.' — It is 13:58.",
  "A colleague is secretly clicking a biro. Click. Click. Click.",
  "Somebody forgot the meeting was today. An awkward late entrance.",
  "The team lead reads out an email. Word for word.",
  "A chair squeaks with every movement.",
  "Somebody asks: 'could we explain that again from the beginning?'",
  "The lights go out. Motion sensor. Somebody has to wave.",
  "A colleague is mirroring their screen. You can see their Slack DMs.",
  "'Next steps?' — Deathly silence.",
  "Somebody counts the attendees out loud. 'Is anyone still missing?'",
  "A colleague types so loudly it sounds like hail.",
  "The clock ticks audibly. Every second feels like a minute.",
  "Somebody whispers into their phone. Everyone pretends not to hear.",
  "The marketing guru says 'low-hanging fruit'. Everyone takes a sip.",
  "An attendee has frozen. Their face holds still for two minutes.",
  "Somebody asks a question that was answered five minutes ago.",
  // Further flavour
  "A post-it falls off the whiteboard. Nobody picks it up.",
  "Somebody yawns contagiously. Three more follow.",
  "The intern is writing everything down. EVERYTHING. Even the pauses.",
  "'I think we're going round in circles.' — 'No, that was a square.'",
  "A colleague has their children in the background. They are playing drums.",
  "Somebody asks for the Wi-Fi password. Third time this week.",
  "The Scrum Master draws a burndown chart on paper. It only goes up.",
  "A smoke alarm chirps quietly in the background. Everybody ignores it.",
  "Somebody drinks from a mug reading 'I hate Mondays'. It is Thursday.",
  "'We need more alignment.' — Nobody knows on what.",
  "A colleague has their camera on but is visibly staring at a second monitor.",
  "Somebody says 'let's circle back'. An internal scream echoes through the room.",
  "The head of department shows a pie chart. Every segment is labelled 'other'.",
  "A colleague answers a question that was asked twenty minutes ago.",
  "Somebody forgot to turn off their virtual background. They are 'on a beach'.",
  "The boss says 'thank you for your time'. It sounds like a threat.",
  "'Quick question...' — The question takes seven minutes.",
  "A delivery driver rings the bell of a remote attendee. Loudly. Repeatedly.",
  "Somebody shifts their chair back and forth. The sound goes through you.",
  "The designer opens Figma. The laptop freezes.",
  "'I have a slide on that...' — There are twelve slides.",
  "A colleague mutters 'this could have been an email.' — Everyone nods internally.",
  "Somebody didn't disable their screensaver. Flying toasters.",
  "The Product Owner spontaneously changes the priorities. Fourth time today.",
  "A colleague is eating carrots. It sounds like a beaver at work.",
  "'Any more questions?' — Everybody studies the table.",
  "A window won't close. Or open. Nobody is quite sure.",
  "Somebody says 'let's take this offline' and means 'be quiet'.",
  "The team lead is taking notes on his tablet. It is sudoku.",
  "A colleague suddenly laughs. They received a Slack message.",
  "Somebody breathes into the mic so heavily it sounds like a storm.",
  "'Let me share my screen.' — Forty-seven tabs. One of them is a shopping site.",
  "The HR manager mentions 'wellbeing'. Everyone gets more tired.",
  "A chair collapses. The colleague acts as though nothing happened.",
  "Somebody is humming quietly. It is yesterday's earworm.",
  "The boss asks: 'has anyone got another point?' — Please. No. Please don't.",
  "'We'll discuss that in the next meeting.' — There is always a next meeting.",
  "A colleague photographs the whiteboard. With flash. Everyone blinks.",
  "Somebody has fourteen browser tabs open. All of them make noise.",
  "Coffee from the machine now costs 50 cents more. Outrage.",
  "A colleague says 'proactive'. The mental buzzword bingo card is nearly full.",
  "Somebody sneezes so loudly that everyone flinches.",
  "'Quick poll...' — It takes longer than the actual topic.",
  "The intern asks whether the meeting could be recorded. Panic.",
  "A colleague has lost an AirPod. The whole department joins the search.",
  "Somebody puts their coffee down. On their neighbour's laptop.",
  "The Scrum Master says 'timeboxed'. The meeting is already twenty minutes over.",
  "'How do you all see it?' — Silence. Eyes on phones.",
  "A colleague takes their shoes off. Nobody says anything. Everybody suffers.",
  "Somebody forgot the agenda. There was one?",
  "The projector is aimed at the wrong wall. The talk continues regardless.",
  "A colleague describes their weekend. At length. Nobody asked.",
  "Somebody spills their coffee. Chairs scrape. Paper towels fly.",
  // Shorter interjections
  "A phone rings somewhere. Nobody answers.",
  "Silence. Awkward.",
  "A collective sigh moves through the room.",
  "Somebody suppresses a yawn. Unsuccessfully.",
  "The projector flickers briefly. Everyone hopes.",
  "A quiet 'when is this over?' is audible.",
  "Somebody clicks their biro. Click. Click.",
  "A stomach rumbles loudly. Everyone pretends not to have heard.",
  "The air conditioning makes a strange noise.",
  "A colleague looks pointedly at the clock.",
  "Somebody whispers: 'I can't do this any more.'",
  "A cough. Then another. Domino effect.",
  "The smell of coffee from the corridor is distractingly good.",
  "A chair squeaks mercilessly.",
  "Somebody hastily types a message under the table.",
  "A phone vibrates. It does not stop.",
  "Somebody laughs loudly outside. Envy sets in.",
  "The printer next door starts up. Ratatatata.",
  "A colleague nods intently. Are they asleep?",
  "Somebody clears their throat. Three times. Loudly.",
  "The door opens. Wrong room. The door closes.",
  "Quiet snoring? No, the radiator. Probably.",
  "A post-it sails gently from the whiteboard to the floor.",
  "Somebody mutters 'agenda item 47...' — There are only five.",
  "The coffee machine in the corridor gurgles ominously.",
  "A phone alarm goes off. 'DRINK WATER!' it says on the display.",
  "A colleague stares intently at their empty notepad.",
  "The radiator is knocking. Morse code?",
  "A discreet smell of microwaved lunch drifts in.",
  "Somebody sorts papers noisily. Now. Really, now.",
  // The new way of working
  "The AI notetaker has joined the call. It is now the most attentive attendee.",
  "The meeting bot is taking minutes. It has noted three things nobody said.",
  "'I had the AI prepare something quickly.' — It is fourteen slides.",
  "Somebody is visibly reading an AI answer off their screen. Including 'Certainly!'.",
  "The AI summary of the last meeting is longer than the meeting.",
  "'We have to think AI-first here.' Nobody asks what that means.",
  "'It's 100% AI-generated, but I looked it over.' — He did not.",
  "The AI proposes a follow-up meeting as the next step. Naturally.",
  "Somebody has an AI avatar as a profile picture. It has eleven fingers and excellent self-esteem.",
  "'Couldn't we put an agent on that?' — On a two-page set of minutes.",
  "The transcript calls you 'Martin' throughout. Your name is not Martin.",
  "The prompt is being optimised live in the meeting. Round four.",
  "'The AI validated it.' — The AI wrote it.",
  "Marketing shows an AI-generated roadmap. Q5 looks promising.",
  "Somebody asks whether the AI could have run the meeting. Embarrassed silence.",
  "The data centre allowance is used up. Everyone has to think for themselves today.",
  "The assistant wrote the agenda. Item 1: 'Discuss the agenda'.",
  "'I cross-checked that with the AI.' — The AI suggested it.",
  "Somebody asks whether the transcript is GDPR-compliant. A long silence.",
  "The bot summarises: 'It was discussed.' That does cover it.",
  "'The AI says we're on plan.' The plan is also from the AI.",
  "Two people read AI answers to each other. Nobody intervenes.",
  "The assistant proposes 'further alignment'. For the fourth time.",
  "The deck has three typos. All three are generated.",
  "'We built an agent for that.' — It is a cron job.",
  "The notetaker has added itself as an attendee.",
  "Somebody thanks the chatbot. Out of habit.",
  "'Can't the AI have it by tomorrow?' The AI has already agreed to.",
];

// ---- Random encounters between meetings ----

export const RANDOM_ENCOUNTERS: RandomEncounter[] = [
  {
    id: "putzfrau",
    name: "Olga from Cleaning",
    emoji: "🧹",
    intro: "Olga has blocked the corridor with her trolley. She gives you a meaningful look.",
    choices: [
      {
        label: "💬 Make small talk",
        message: "Olga is delighted by the conversation and hands you an energy drink from her trolley.",
        effects: { fatigue: -20 },
      },
      {
        label: "🙋 Say hello politely and pass",
        message: "Olga nods, satisfied. 'You're all right. The others always ignore me.'",
        effects: { addRandomPositivePerk: true },
        alternateMessage: "Olga looks disappointed. 'Always in such a hurry...' She runs the trolley over your foot.",
        alternateEffects: { fatigue: 5 },
        alternateChance: 0.25,
      },
    ],
  },
  {
    id: "hausmeister",
    name: "Werner the Caretaker",
    emoji: "🔧",
    intro: "Werner is standing in the corridor with a ladder. 'Hey, can you hold this a second?'",
    choices: [
      {
        label: "🧑‍🔧 Hold the ladder",
        message: "Werner thanks you warmly. 'Here, take this as a thank you.' He gives you a chocolate bar.",
        effects: { fatigue: -10, salary: 25 },
      },
      {
        label: "⏳ No time, sorry!",
        message: "Werner mutters something about 'young people today'. Later, the heating in your office is off.",
        effects: { addRandomNegativePerk: true },
        alternateMessage: "Werner shrugs. Nothing happens.",
        alternateEffects: {},
        alternateChance: 0.4,
      },
    ],
  },
  {
    id: "postbote",
    name: "Rainer the Postman",
    emoji: "📦",
    intro: "Rainer is standing in front of you with a mysterious parcel. 'For you! ...I think.'",
    choices: [
      {
        label: "📦 Accept the parcel",
        message: "The parcel contains an ergonomic wrist rest. You feel rested!",
        effects: { fatigue: -25 },
        alternateMessage: "The parcel contains 200 business cards with the wrong name. Useless.",
        alternateEffects: { fatigue: 5 },
        alternateChance: 0.3,
      },
      {
        label: "❌ Refuse it",
        message: "Rainer shrugs and walks on. Nothing happens.",
        effects: {},
      },
    ],
  },
  {
    id: "kantine",
    name: "Helga from the Kantine",
    emoji: "🍲",
    intro: "Helga has leftovers. 'Do you want something? There's goulash or salad.'",
    choices: [
      {
        label: "🍛 Take the goulash",
        message: "The goulash is surprisingly good! You feel fortified.",
        effects: { fatigue: -15, salary: 0 },
        alternateMessage: "The goulash was yesterday's. You feel unwell.",
        alternateEffects: { addRandomNegativePerk: true },
        alternateChance: 0.2,
      },
      {
        label: "🥗 Take the salad",
        message: "Healthy eating! You feel light and focused.",
        effects: { fatigue: -8, addRandomPositivePerk: true },
      },
    ],
  },
  {
    id: "praktikant",
    name: "The New Intern",
    emoji: "👶",
    intro: "A new intern is standing lost in the corridor. 'Excuse me, where is conference room B?'",
    choices: [
      {
        label: "📍 Show the way",
        message: "The intern is grateful. 'Thanks! Here, have some sweets!' Later he tells the boss how kind you were.",
        effects: { salary: 30, fatigue: -5 },
      },
      {
        label: "🤷 Point the wrong way",
        message: "You send him to the server room. Funny, but it gets out.",
        effects: { salary: -20 },
        alternateMessage: "You send him to the server room. Nobody notices. Pure slapstick!",
        alternateEffects: { fatigue: -15 },
        alternateChance: 0.5,
      },
    ],
  },
  {
    id: "kaffeemaschine",
    name: "Broken Coffee Machine",
    emoji: "☕",
    intro: "The coffee machine in the corridor is making odd noises and flashing red.",
    choices: [
      {
        label: "🔧 Try to fix it",
        message: "After a bit of fiddling it works again! You reward yourself with a coffee.",
        effects: { fatigue: -30 },
        alternateMessage: "You make it worse. Coffee sprays everywhere. Embarrassing.",
        alternateEffects: { fatigue: 10, salary: -15 },
        alternateChance: 0.35,
      },
      {
        label: "🚶 Ignore it",
        message: "You walk on. The machine explodes later. Not your problem.",
        effects: {},
      },
    ],
  },
  {
    id: "coach",
    name: "Sven the Agile Coach",
    emoji: "🧘",
    intro: "Sven intercepts you in the corridor. 'Have you got two minutes for a retro?'",
    choices: [
      {
        label: "🗣️ Join in",
        message: "Sven gives you surprisingly good advice. You feel focused.",
        effects: { removeNegativePerk: true, fatigue: -10 },
      },
      {
        label: "🏃 Make a run for it",
        message: "You escape Sven's retro trap. But he remembers this...",
        effects: {},
        alternateMessage: "You run, but Sven is faster. Compulsory retro! It wasn't that bad, actually.",
        alternateEffects: { fatigue: -5 },
        alternateChance: 0.3,
      },
    ],
  },
  {
    id: "lieferant",
    name: "Delivery Driver",
    emoji: "🛵",
    intro: "A delivery driver is in the entrance with office supplies. 'Can you sign for this?'",
    choices: [
      {
        label: "✍️ Sign for it",
        message: "You sign and get a branded biro for your trouble. Nice.",
        effects: { salary: 10 },
        alternateMessage: "You have accidentally signed for 500 toner cartridges. HR is furious.",
        alternateEffects: { salary: -50 },
        alternateChance: 0.15,
      },
      {
        label: "❌ Walk on",
        message: "Not your job. You walk on.",
        effects: {},
      },
    ],
  },
  {
    id: "aufzug",
    name: "The Lift Is Stuck",
    emoji: "🚷",
    intro: "You step into the lift and it stops between two floors!",
    choices: [
      {
        label: "📲 Call IT",
        message: "IT needs ten minutes. But you use the break for a power nap.",
        effects: { fatigue: -35 },
      },
      {
        label: "🧗 Climb out",
        message: "You climb through the roof hatch! Action-hero energy. Sadly the security camera saw it.",
        effects: { salary: -25, fatigue: -10 },
        alternateMessage: "You climb heroically through the roof hatch. The boss is impressed!",
        alternateEffects: { salary: 40, fatigue: -20 },
        alternateChance: 0.4,
      },
    ],
  },
  {
    id: "feueralarm",
    name: "False Alarm",
    emoji: "🚨",
    intro: "The fire alarm goes off! Everyone runs outside.",
    choices: [
      {
        label: "🚶 Evacuate in an orderly fashion",
        message: "The fresh air gives you new energy. The false alarm was a blessing.",
        effects: { fatigue: -20, addRandomPositivePerk: true },
      },
      {
        label: "🎰 Stay at your desk",
        message: "Everyone is outside, you have fifteen minutes of quiet! Perfect for procrastinating.",
        effects: { fatigue: -40 },
        alternateMessage: "The fire safety officer finds you. Written warning.",
        alternateEffects: { salary: -35 },
        alternateChance: 0.25,
      },
    ],
  },
  {
    id: "serverraum",
    name: "Access to the Server Room",
    emoji: "🖥️",
    intro: "The server room door is open. It is cool and quiet in there.",
    choices: [
      {
        label: "❄️ Go in and cool off",
        message: "The cold refreshes you. You also find a USB stick with useful tools on it.",
        effects: { fatigue: -25, dropRandomPowerup: true },
      },
      {
        label: "🚪 Close the door and walk on",
        message: "Responsible! The facility manager thanks you by email later.",
        effects: { salary: 20 },
      },
    ],
  },
  {
    id: "pizzabote",
    name: "Pizza Delivery in the Foyer",
    emoji: "🍕",
    intro: "A pizza courier stands baffled in the foyer. 'Do you know a Max Müller? Nobody is answering.'",
    choices: [
      {
        label: "🍕 Take the pizza yourself",
        message: "You take the pizza. There is no Max Müller here at all. Result!",
        effects: { fatigue: -30, dropRandomPowerup: true },
        alternateMessage: "You take the pizza — but it is pineapple and anchovy. Revolting.",
        alternateEffects: { fatigue: -5 },
        alternateChance: 0.2,
      },
      {
        label: "📱 Go and find Max Müller",
        message: "You search for ten minutes. There is no Max Müller. The courier gives you the pizza anyway.",
        effects: { fatigue: -15 },
      },
    ],
  },
  {
    id: "ceoImAufzug",
    name: "The CEO in the Lift",
    emoji: "👔",
    intro: "You are alone in the lift with the CEO. Awkward silence.",
    choices: [
      {
        label: "💬 Deliver your elevator pitch",
        message: "The CEO is impressed! 'Good idea, send me an email.' Salary bonus!",
        effects: { salary: 100 },
        alternateMessage: "The CEO isn't listening. He is scrolling on his phone. Excruciating silence.",
        alternateEffects: {},
        alternateChance: 0.35,
      },
      {
        label: "🤐 Say nothing",
        message: "The CEO nods at you and gets out. Nothing happens. Or does it? He remembers faces...",
        effects: { addRandomPositivePerk: true },
        alternateMessage: "The CEO mutters 'and you are?' Not ideal.",
        alternateEffects: { addRandomNegativePerk: true },
        alternateChance: 0.25,
      },
    ],
  },
  {
    id: "kabelchaos",
    name: "Cable Chaos",
    emoji: "🔌",
    intro: "A bundle of cables hangs out of the ceiling. Somebody has written 'DO NOT TOUCH' on it.",
    choices: [
      {
        label: "🔌 Sort the cables",
        message: "You sort the cables like a professional. The network is back! Grateful colleagues!",
        effects: { salary: 40, fatigue: -10, addRandomPositivePerk: true },
        alternateMessage: "You pull the wrong cable. The Wi-Fi on the entire floor goes down.",
        alternateEffects: { salary: -30, addRandomNegativePerk: true },
        alternateChance: 0.3,
      },
      {
        label: "📸 Photograph it and report it to IT",
        message: "IT is grateful. 'Finally somebody reports one of these.' Small bonus.",
        effects: { salary: 15 },
      },
    ],
  },
  {
    id: "kaffeeAutomat",
    name: "Coffee Machine Jackpot",
    emoji: "☕",
    intro: "The coffee machine is dispensing endlessly! A queue forms.",
    choices: [
      {
        label: "☕ Grab the free coffee",
        message: "Three cups free! You are wide awake and even brought one for the team lead.",
        effects: { fatigue: -35, dropRandomPowerup: true },
      },
      {
        label: "🛠️ Call a technician",
        message: "You call the technician. He fixes it and gives you a coffee for your trouble.",
        effects: { fatigue: -15, salary: 10 },
      },
    ],
  },
  {
    id: "verloreneUnterlagen",
    name: "Lost Documents",
    emoji: "📂",
    intro: "You find confidential documents on the copier. 'Salary table Q3'...",
    choices: [
      {
        label: "👀 Take a quick look",
        message: "You see that the intern earns more than you. Motivation drops. But at least now you know.",
        effects: { addRandomNegativePerk: true, salary: 50 },
      },
      {
        label: "📤 Hand them back unread",
        message: "The head of HR is grateful. 'Very principled of you!' Bonus rating.",
        effects: { salary: 60, addRandomPositivePerk: true },
      },
    ],
  },
  {
    id: "spontaneMassage",
    name: "Spontaneous Massage",
    emoji: "💆",
    intro: "A mobile massage service is set up in the foyer. 'Free trial massage for staff!'",
    choices: [
      {
        label: "💆 Take the neck massage",
        message: "Five minutes of pure relaxation. You feel reborn!",
        effects: { fatigue: -40, removeNegativePerk: true },
      },
      {
        label: "⏰ No time, thanks",
        message: "You are too stressed for an anti-stress massage. The irony is not lost on you.",
        effects: {},
      },
    ],
  },
  {
    id: "mysterioeserKoffer",
    name: "Mysterious Briefcase",
    emoji: "💼",
    intro: "An unattended briefcase sits beside your desk. There is a note: 'For the hardest worker'.",
    choices: [
      {
        label: "💼 Open the briefcase",
        message: "Inside is a survival kit: energy drink, headphones and emergency snacks!",
        effects: { fatigue: -20, dropRandomPowerup: true },
        alternateMessage: "Inside are only old company T-shirts in XXL. Warm, at least.",
        alternateEffects: { fatigue: -5 },
        alternateChance: 0.25,
      },
      {
        label: "🚨 Call security",
        message: "Security gives the all-clear. It was a promotional gift. You get a thank you.",
        effects: { salary: 25 },
      },
    ],
  },
  {
    id: "heimlicherRaum",
    name: "The Secret Break Room",
    emoji: "🚪",
    intro: "You discover a hidden break room behind the archive. Sofa, snacks, silence.",
    choices: [
      {
        label: "🛋️ Take a power nap",
        message: "Twenty minutes on the sofa. The best decision of the day!",
        effects: { fatigue: -45 },
        alternateMessage: "You sleep too long! The head of department finds you. Uncomfortable.",
        alternateEffects: { fatigue: -20, salary: -40 },
        alternateChance: 0.2,
      },
      {
        label: "🍫 Take the snacks",
        message: "You fill your pockets. Chocolate bars for the rest of the day!",
        effects: { fatigue: -15, dropRandomPowerup: true },
      },
    ],
  },
  {
    id: "buerohund",
    name: "Max the Office Dog",
    emoji: "🐕",
    intro: "A golden retriever is running down the corridor! Somebody brought their dog in.",
    choices: [
      {
        label: "🐕 Stroke the dog",
        message: "Max wags enthusiastically. Your cortisol drops instantly. The best therapy there is!",
        effects: { fatigue: -30, removeNegativePerk: true },
      },
      {
        label: "📷 Photo for the team chat",
        message: "The photo goes viral in the company Slack. You are the hero of the day!",
        effects: { addRandomPositivePerk: true, salary: 15 },
      },
    ],
  },
  {
    id: "kiBeauftragter",
    name: "Marcel, AI Transformation Lead",
    emoji: "🤖",
    intro: "Marcel was appointed 'AI Transformation Lead' last week. He isn't entirely sure what that means yet — but he already has slides.",
    choices: [
      {
        label: "🎞️ Look at the slides",
        message: "Marcel shows you 40 slides in five minutes. Afterwards he buys you a coffee out of his innovation budget.",
        effects: { fatigue: -15, salary: 20 },
      },
      {
        label: "💡 Give him a real use case",
        message: "Marcel notes down your suggestion and calls you his sparring partner from now on. That opens doors.",
        effects: { addRandomPositivePerk: true },
        alternateMessage: "Marcel records you as the owner of the use case. Congratulations.",
        alternateEffects: { addRandomNegativePerk: true },
        alternateChance: 0.35,
      },
      {
        label: "🚪 'I've got a meeting in a minute'",
        message: "Marcel nods understandingly. He has one too. Everybody has one in a minute.",
        effects: {},
      },
    ],
  },
];

// ---- Direction change: what somebody says when the round flips ----

export const DIRECTION_CHANGE_QUOTES = [
  "Hang on, let's start from the beginning again!",
  "Oops, other way round!",
  "We're reversing the round!",
  "Could we change the order?",
  "I think we should go round the other way.",
  "The notetaker changed the order. Nobody argues with a bot.",
  "'The AI suggests we go round the other way.'",
];

// ---- Being called on ----

export function addressPhrases(
  { playerName, playerRole, hint }: AddressPhraseContext,
): Record<AddressMethod, string[]> {
  return {
    name: [
      `"${hint(playerName)}, what do you make of that?"`,
      `"${hint(playerName)}, your view?"`,
      `"Ah ${hint(playerName)}, you were the one who last time..."`,
      `"Hey ${hint(playerName)}, quick question..."`,
      `"What do you think, ${hint(playerName)}?"`,
    ],
    role: [
      `"Our ${hint(playerRole)} will be able to judge that."`,
      `"Well, as a ${hint(playerRole)} you'd know all about this..."`,
      `"That's one for our ${hint(playerRole)}, surely!"`,
      `"Maybe our ${hint(playerRole)} has something to add?"`,
    ],
    informal: [
      `"${hint("You")} at the back, what do you reckon?"`,
      `"And ${hint("you")}? All clear so far?"`,
      `"${hint("You're")} the one responsible for this, aren't you?"`,
      `"Hey, ${hint("you")} — your read on this?"`,
    ],
  };
}
